import InspirationImage from "@/components/molecules/inspiration-image";
import { Locales } from "@/lib/i18n";
import { getPermalink } from "@/lib/i18n/functions";
import { InspirationCategoryDocument, TablesDocument } from "@/prismicio-types";

const flexPatterns = [
  ["1", "2"],
  ["1", "1"],
  ["2", "1"],
  ["1", "1"],
];

type BentoGridItem = {
  id?: string;
  image: string;
  altText: string;
  link: string;
  locale: Locales;
  title: string;
  display?: number;
  alternates?: { locale: Locales; id: string }[];
};

type InspirationBentoGridProps = {
  inspirationCategories: BentoGridItem[];
};

export default function InspirationBentoGrid({ inspirationCategories }: InspirationBentoGridProps) {
  const rows = Math.ceil(inspirationCategories.length / 2);

  return (
    <section className="container px-4 md:my-7.5 md:px-7.5">
      {Array.from({ length: rows }).map((_, rowIndex) => {
        const pattern = flexPatterns[rowIndex % flexPatterns.length];
        const item1 = inspirationCategories[rowIndex * 2];
        const item2 = inspirationCategories[rowIndex * 2 + 1];

        if (!item1 && !item2) return null;

        return (
          <div key={rowIndex} className="mt-5 flex flex-col gap-5 md:flex-row">
            {item1 && (
              <div className={pattern[0] === "2" ? "md:w-2/3" : "md:w-1/2"}>
                <InspirationImage
                  image={item1.image}
                  imageAlt={item1.altText}
                  link={item1.link}
                  variant="category"
                  category={item1.title}
                  locale={item1.locale}
                />
              </div>
            )}

            {item2 && (
              <div className={pattern[1] === "2" ? "md:w-2/3" : "md:w-1/2"}>
                <InspirationImage
                  image={item2.image}
                  imageAlt={item2.altText}
                  link={item2.link}
                  variant="category"
                  category={item2.title}
                  locale={item2.locale}
                />
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

export const mapInspirationCategories = (
  categories: InspirationCategoryDocument[],
  locale: Locales
): BentoGridItem[] => {
  return categories
    .map((doc) => {
      const imageField = doc.data.featuredImage;
      if (!imageField?.url) return null;

      return {
        id: doc.id,
        image: imageField.url,
        altText: imageField.alt || doc.data.title || "",
        link: `${getPermalink(locale, "inspiration_archive")}/${doc.uid}`,
        locale,
        title: doc.data.title || "",
        display: doc.data.display || 9999,
        alternates: doc.alternate_languages.map((alt) => ({
          locale: alt.lang,
          id: alt.id,
        })),
      };
    })
    .filter(Boolean) as BentoGridItem[];
};

export const mapTables = (categories: TablesDocument[], locale: Locales): BentoGridItem[] => {
  return categories
    .map((doc) => {
      const imageField = doc.data.featured_image;
      if (!imageField?.url) return null;

      return {
        image: imageField.url,
        altText: imageField.alt || doc.data.title || "",
        link: `${getPermalink(locale, "tables")}/${doc.uid}`,
        locale,
        title: doc.data.title || "",
      };
    })
    .filter(Boolean) as BentoGridItem[];
};
